import React, { useState } from 'react';
import { Link2, Calendar, List, BarChart3, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import UpgradePrompt from './UpgradePrompt';
import PlatformConnections from './PlatformConnections';
import PostCalendar from './PostCalendar';
import PostList from './PostList';
import SocialAnalytics from './SocialAnalytics';
import AIContentGenerator from './AIContentGenerator';

const TABS = [
    { id: 'accounts', label: 'Accounts', icon: Link2 },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'posts', label: 'Posts', icon: List },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'ai', label: 'AI Assistant', icon: Sparkles },
];

const SocialMediaManager = ({
    userEmail,
    subscription,
    featureFlags = {},
    onUpgradePlan
}) => {
    const [activeTab, setActiveTab] = useState('accounts');

    // Feature gate check
    if (!featureFlags.hasSocialMedia) {
        return <UpgradePrompt onUpgrade={onUpgradePlan} />;
    }

    const renderTabContent = () => {
        switch (activeTab) {
            case 'accounts':
                return <PlatformConnections subscription={subscription} />;
            case 'calendar':
                return <PostCalendar />;
            case 'posts':
                return <PostList />;
            case 'analytics':
                return <SocialAnalytics />;
            case 'ai':
                return <AIContentGenerator />;
            default:
                return <PlatformConnections subscription={subscription} />;
        }
    };

    return (
        <div className="flex flex-col h-full bg-white dark:bg-slate-950">
            {/* Header with Tabs */}
            <div className="px-6 lg:px-8 pt-6 pb-0 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                            Social Media
                        </h1>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                            Manage your social media presence across platforms
                        </p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="flex items-center gap-1 overflow-x-auto pb-px -mb-px">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                activeTab === tab.id
                                    ? 'border-yellow-500 text-yellow-600 dark:text-yellow-400'
                                    : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-auto">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.15 }}
                        className="h-full"
                    >
                        {renderTabContent()}
                    </motion.div>
                </AnimatePresence>
            </div>
        </div>
    );
};

export default SocialMediaManager;
